import { previewRecipientCount, TenantMemberOrTeamDisplayData } from '@kudos/http-client'
import { useMemberPoints } from '@shared/hooks/useMemberPoints'
import { getSendToFromTenantMemberAndTeams } from '@shared/utils/getSendToFromTenantMemberAndTeams'
import { useMutation } from '@tanstack/react-query'
import { useEffect, useMemo, useState } from 'react'
import PointsEstimateInfoDisplay from './PointsEstimateInfoDisplay'

type PointsEstimateDisplayProps = {
  amount: number
  sendTo: TenantMemberOrTeamDisplayData[]
  onRecipientCountChange?: (count: number) => void
  onAllocationPointsChange?: (points: number) => void
  backgroundColor?: string
}

const PointsEstimateDisplay: React.FC<PointsEstimateDisplayProps> = ({
  amount,
  sendTo,
  onRecipientCountChange,
  onAllocationPointsChange,
  backgroundColor,
}) => {
  const [recipientCount, setRecipientCount] = useState<number>(0)
  const { allocationPoints, arePointsLoaded } = useMemberPoints()

  useEffect(() => {
    if (arePointsLoaded) {
      onAllocationPointsChange?.(allocationPoints)
    }
  }, [allocationPoints, arePointsLoaded, onAllocationPointsChange])

  const { mutateAsync: previewRecipients, isPending: isPreviewPending } = useMutation({
    mutationFn: previewRecipientCount,
  })

  useEffect(() => {
    if (sendTo.length > 0 && !isPreviewPending) {
      const sendToData = getSendToFromTenantMemberAndTeams(sendTo)
      previewRecipients({ sendTo: sendToData }).then(({ recipientCount }) => {
        setRecipientCount(recipientCount)
        onRecipientCountChange?.(recipientCount)
      })
    } else {
      setRecipientCount(0)
      onRecipientCountChange?.(0)
    }
  }, [sendTo.length])

  const totalSpend = useMemo(() => {
    if (recipientCount > 0 && amount > 0) {
      return recipientCount * amount
    }
    return 0
  }, [recipientCount, amount])

  const showEstimate = useMemo(() => {
    return amount > 0 && recipientCount > 0 && totalSpend <= allocationPoints
  }, [amount, recipientCount, totalSpend, allocationPoints])

  if (!arePointsLoaded) {
    return null
  }

  return (
    <PointsEstimateInfoDisplay
      amount={amount}
      recipientCount={recipientCount}
      allocationPoints={allocationPoints}
      totalSpend={totalSpend}
      showEstimate={showEstimate}
      backgroundColor={backgroundColor}
    />
  )
}

export default PointsEstimateDisplay
